<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main Content -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

    <!-- Topbar -->
    <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
      <HrTopbar
        :page-title="currentPageTitle"
        :is-sidebar-open="isSidebarOpen"
        :is-dark-mode="isDarkMode"
        @toggle-sidebar="toggleSidebar"
        @toggle-dark-mode="toggleDarkMode"
      />
    </header>

    <!-- Page Content -->
    <div class="p-6 mt-16">

      <!-- Top Bar -->
      <div class="flex justify-between items-center mb-8 p-4">

        <input
          v-model="search"
          type="text"
          placeholder="Search module..."
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring-2 focus:ring-secondary dark:bg-gray-700 dark:text-white"
        />

        <button
          @click="openCreate"
          class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow flex items-center gap-2"
        >
          + Add Module
        </button>

      </div>

      <!-- Modules Grid -->
      <div
        v-if="filteredModules.length"
        class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
      >

        <div
          v-for="mod in filteredModules"
          :key="mod.id"
          class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition group"
        >

          <div class="flex justify-between items-start mb-4">
            <div class="flex items-center space-x-3">
              <div class="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-lg">
                {{ mod.name.charAt(0).toUpperCase() }}
              </div>

              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">
                  {{ mod.name }}
                </h3>
              </div>
            </div>
          </div>

          <div class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {{ getPermissionCount(mod.id) }} permissions
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-2 text-sm">
            <button
              @click="openPermissionManager(mod)"
              class="text-[#035a76] hover:underline flex items-center gap-1 font-medium"
            >
              <i class="fas fa-key"></i> Manage Permissions
            </button>
            <div class="flex justify-between pt-2 border-t dark:border-gray-700">
              <button
                @click="editModule(mod)"
                class="text-blue-600 hover:underline flex items-center gap-1"
              >
                <i class="fas fa-edit"></i> Edit
              </button>
              <button
                @click="deleteModule(mod.id)"
                class="text-red-500 hover:underline flex items-center gap-1"
              >
                <i class="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>

        </div>

      </div>

      <!-- Empty State -->
      <div v-else class="text-center text-gray-400 mt-20">
        No modules created yet
      </div>

      <!-- ====================== MODALS ====================== -->

      <!-- 1. Module Create/Edit Modal -->
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">
            <h3 class="text-lg font-semibold text-[#035a76] mb-6">
              {{ editing ? 'Edit Module' : 'Create New Module' }}
            </h3>
            <form @submit.prevent="saveModule" class="space-y-4">
              <input
                v-model="form.name"
                type="text"
                placeholder="Module Name (e.g. manpower, assessment)"
                required
                class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white focus:outline-none"
              />
              <div class="flex justify-end space-x-3 pt-4">
                <button type="button" @click="closeModal" class="px-5 py-2 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" class="px-5 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">
                  Save Module
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>

      <!-- 2. Permission Manager Modal (NEW - Client-side filtered) -->
      <Transition name="modal">
        <div v-if="showPermissionModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[520px] shadow-lg max-h-[85vh] overflow-y-auto">

            <div class="flex justify-between items-start mb-4">
              <div>
                <h3 class="text-lg font-semibold text-[#035a76]">Permissions for</h3>
                <p class="text-gray-600 dark:text-gray-400">{{ selectedModule?.name }}</p>
              </div>
              <button @click="closePermissionModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <i class="fas fa-times text-xl"></i>
              </button>
            </div>

            <!-- Add Permission Button -->
            <button
              @click="openAddPermissionInline"
              class="w-full mb-6 bg-[#74bd54] text-white py-2.5 rounded-lg text-sm hover:bg-[#63a43f] transition flex items-center justify-center gap-2"
            >
              <i class="fas fa-plus"></i> Add Permission
            </button>

            <!-- Permissions List (Client-side filtered) -->
            <div v-if="filteredModulePermissions.length" class="space-y-3 mb-6">
              <div
                v-for="perm in filteredModulePermissions"
                :key="perm.id"
                class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg flex justify-between items-center"
              >
                <div>
                  <p class="font-medium text-gray-800 dark:text-white">{{ perm.name }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ perm.label || 'No description' }}</p>
                </div>
                <div class="flex gap-3 text-sm">
                  <button @click="editPermissionInline(perm)" class="text-blue-600 hover:underline">Edit</button>
                  <button @click="deletePermissionInline(perm.id)" class="text-red-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 text-gray-400">
              No permissions assigned to this module yet
            </div>

            <div class="flex justify-end pt-4 border-t dark:border-gray-700">
              <button @click="closePermissionModal" class="px-5 py-2 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400">
                Close
              </button>
            </div>

          </div>
        </div>
      </Transition>

      <!-- 3. Inline Permission Form Modal -->
      <Transition name="modal">
        <div v-if="showPermissionFormModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[440px] shadow-lg">
            <h3 class="text-lg font-semibold text-[#035a76] mb-6">
              {{ editingPermission ? 'Edit Permission' : 'Add Permission' }}
            </h3>
            <form @submit.prevent="saveInlinePermission" class="space-y-5">
              
              <!-- Module ID (hidden, auto-set) -->
              <input type="hidden" v-model="permissionForm.module_id" />

              <!-- Permission Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Permission Name *
                </label>
                <input
                  v-model="permissionForm.name"
                  type="text"
                  placeholder="e.g. view, create, edit_applicant"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white focus:outline-none"
                />
              </div>

              <!-- Label/Description -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <input
                  v-model="permissionForm.label"
                  type="text"
                  placeholder="Human-readable description"
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-3 dark:bg-gray-700 dark:text-white focus:outline-none"
                />
              </div>

              <div class="flex justify-end space-x-3 pt-4">
                <button type="button" @click="closePermissionFormModal" class="px-5 py-2 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" class="px-5 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">
                  Save Permission
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>

    </div>
  </main>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Modules")
const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

// Module state
const showModal = ref(false)
const editing = ref(false)
const search = ref("")
const modules = ref([])
const form = ref({ id: null, name: "" })

// Permission state (MATCHES Permissions.vue structure)
const allPermissions = ref([])  // Fetch ALL permissions once
const showPermissionModal = ref(false)
const showPermissionFormModal = ref(false)
const selectedModule = ref(null)
const editingPermission = ref(false)
const permissionForm = ref({
  id: null,
  module_id: "",
  name: "",
  label: ""
})

// ==================== MODULE CRUD ====================

const fetchModules = async () => {
  try {
    const response = await api.get("Modules.php")
    modules.value = response.data
  } catch (error) {
    console.error("Error fetching Modules:", error)
  }
}

const fetchAllPermissions = async () => {
  try {
    const res = await api.get("Permissions.php")
    allPermissions.value = res.data
  } catch (error) {
    console.error("Error fetching permissions:", error)
  }
}

const filteredModules = computed(() => {
  return modules.value.filter(mod =>
    mod.name.toLowerCase().includes(search.value.toLowerCase())
  )
})

// ✅ Client-side filter: Only permissions for the selected module
const filteredModulePermissions = computed(() => {
  if (!selectedModule.value) return []
  return allPermissions.value.filter(p => p.module_id === selectedModule.value.id)
})

// ✅ Count helper for module card display
const getPermissionCount = (moduleId) => {
  return allPermissions.value.filter(p => p.module_id === moduleId).length
}

const openCreate = () => {
  editing.value = false
  form.value = { id: null, name: "" }
  showModal.value = true
}

const editModule = (mod) => {
  editing.value = true
  form.value = { ...mod }
  showModal.value = true
}

const saveModule = async () => {
  try {
    if (editing.value) {
      await api.put("Modules.php", form.value)
      const index = modules.value.findIndex(m => m.id === form.value.id)
      if (index !== -1) modules.value[index].name = form.value.name
    } else {
      const response = await api.post("Modules.php", form.value)
      modules.value.push({
        id: response.data.id,
        name: response.data.name
      })
    }
    closeModal()
    fetchModules()
  } catch (error) {
    console.error("Error saving module:", error)
    alert("Failed to save module")
  }
}

const deleteModule = async (id) => {
  if (!confirm("Are you sure you want to delete this module?")) return
  try {
    // ✅ Fixed: proper delete payload structure
    await api.delete("Modules.php", { data: { id: id } })
    modules.value = modules.value.filter(m => m.id !== id)
  } catch (error) {
    console.error("Error deleting module:", error)
    alert("Failed to delete module. It may have permissions attached.")
  }
}

const closeModal = () => { showModal.value = false }

// ==================== PERMISSION MANAGER (Client-side filtered) ====================

const openPermissionManager = (module) => {
  selectedModule.value = module
  // No need to re-fetch - allPermissions already loaded
  showPermissionModal.value = true
}

const openAddPermissionInline = () => {
  editingPermission.value = false
  permissionForm.value = {
    id: null,
    module_id: selectedModule.value?.id || "",
    name: "",
    label: ""
  }
  showPermissionFormModal.value = true
}

const editPermissionInline = (perm) => {
  editingPermission.value = true
  // Copy all fields including module_id
  permissionForm.value = { ...perm }
  showPermissionFormModal.value = true
}

const saveInlinePermission = async () => {
  try {
    // Ensure module_id is locked to selected module
    permissionForm.value.module_id = selectedModule.value?.id

    if (editingPermission.value) {
      await api.put("Permissions.php", permissionForm.value)
      // Update local allPermissions array
      const idx = allPermissions.value.findIndex(p => p.id === permissionForm.value.id)
      if (idx !== -1) allPermissions.value[idx] = { ...permissionForm.value }
    } else {
      const res = await api.post("Permissions.php", permissionForm.value)
      allPermissions.value.push({
        id: res.data.id,
        ...permissionForm.value
      })
    }
    closePermissionFormModal()
  } catch (error) {
    console.error("Error saving permission:", error)
    alert("Failed to save permission")
  }
}

const deletePermissionInline = async (id) => {
  if (!confirm("Delete this permission?")) return
  try {
    // ✅ Fixed: proper delete payload structure
    await api.delete("Permissions.php", { data: { id } })
    allPermissions.value = allPermissions.value.filter(p => p.id !== id)
  } catch (error) {
    console.error("Error deleting permission:", error)
    alert("Failed to delete permission")
  }
}

const closePermissionModal = () => {
  showPermissionModal.value = false
  selectedModule.value = null
}

const closePermissionFormModal = () => {
  showPermissionFormModal.value = false
}

// ==================== UTILITIES ====================

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

onMounted(() => {
  fetchModules()
  fetchAllPermissions()  // ✅ Load ALL permissions once (same as Permissions.vue)
})
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}
</style>